import {
  Column,
  ColumnOrderState,
  ColumnPinningState,
  ColumnDef,
  OnChangeFn,
  SortingState,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  ArrowUpDown, Check, ChevronLeft, ChevronRight, Download, GripVertical, MoreHorizontal, Pin,
} from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import cn from "../../utils/styleHelpers";
import EmptyTablePlaceholder from "./EmptyTablePlaceHolder";
import { LegacyColumn, createLegacyTanStackColumns } from "./legacyTanStackAdapter";

type Props = {
  data: any[];
  columns?: ColumnDef<any, any>[];
  legacyColumns?: LegacyColumn[];
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  pageIndex?: number;
  pageSize?: number;
  pageCount?: number;
  onPageIndexChange?: (pageIndex: number) => void;
  totalData?: number;
  itemType?: string;
  noDataTitle?: string;
  noDataMessage?: string;
  onRowClick?: (row: any) => void;
  loading?: boolean;
  height?: number | string;
  showPagination?: boolean;
  enableColumnReorder?: boolean;
  enableColumnPinning?: boolean;
  enableColumnResizing?: boolean;
  surfaceVariant?: "legacy" | "lovable";
};

const getPaginationTokens = (currentPage: number, totalPages: number): Array<number | "ellipsis"> => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const windowStart = Math.max(1, Math.min(currentPage - 2, totalPages - 4));
  const windowPages = Array.from({ length: 5 }, (_, index) => windowStart + index)
    .filter((page) => page >= 1 && page <= totalPages);

  const tokens: Array<number | "ellipsis"> = [];
  if (windowPages[0] > 1) {
    tokens.push(1);
    if (windowPages[0] > 2) tokens.push("ellipsis");
  }

  windowPages.forEach((page) => tokens.push(page));

  const lastWindowPage = windowPages[windowPages.length - 1];
  if (lastWindowPage < totalPages) {
    if (lastWindowPage < totalPages - 1) tokens.push("ellipsis");
    tokens.push(totalPages);
  }

  return tokens;
};

const resolveRowSelectionId = (row: any, index: number): string => {
  if (row?.randomId) return String(row.randomId);
  if (row?.id) return String(row.id);
  if (row?.["accounts.external_id"]) return String(row["accounts.external_id"]);
  if (row?.["users.external_id"]) return String(row["users.external_id"]);
  if (row?.["contacts.external_id"]) return String(row["contacts.external_id"]);
  return `row-${index}`;
};

const toExportValue = (value: any): string => {
  if (value === undefined || value === null) return "";
  if (Array.isArray(value)) return value.map((item) => toExportValue(item)).join(" | ");

  if (typeof value === "object") {
    if (typeof value.name === "string") return value.name;
    if (typeof value.externalId === "string") return value.externalId;
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }

  return String(value);
};

const getColumnHeaderLabel = (column: Column<any, any>) => {
  const { header } = column.columnDef;
  if (typeof header === "string") return header;
  if (typeof header === "number") return String(header);
  return String(column.id || "");
};

const getColumnRawValue = (column: Column<any, any>, row: any, rowIndex: number) => {
  const { accessorFn, accessorKey } = column.columnDef as any;
  if (typeof accessorFn === "function") return accessorFn(row, rowIndex);

  if (typeof accessorKey === "string" && accessorKey.length) return row?.[accessorKey];

  return row?.[column.id];
};

const escapeCsvCell = (value: string) => `"${value.replace(/"/g, "\"\"")}"`;

type TableCheckboxProps = {
  checked: boolean;
  indeterminate?: boolean;
  onChange: () => void;
  surfaceVariant?: "legacy" | "lovable";
};

const TableCheckbox = ({
  checked,
  indeterminate = false,
  onChange,
  surfaceVariant = "legacy",
}: TableCheckboxProps) => (
  <button
    type="button"
    role="checkbox"
    aria-checked={indeterminate ? "mixed" : checked}
    onClick={(event) => {
      event.stopPropagation();
      onChange();
    }}
    className={cn(
      "h-4 w-4 shrink-0 rounded-[4px] border transition-colors inline-flex items-center justify-center",
      checked || indeterminate
        ? "bg-gray-900 border-gray-900 text-white"
        : surfaceVariant === "lovable"
          ? "bg-card border-border text-transparent hover:border-muted-foreground/40"
          : "bg-white border-gray-300 text-transparent hover:border-gray-400",
    )}
  >
    {indeterminate ? (
      <span className="h-[2px] w-2 rounded bg-white" />
    ) : (
      <Check className={`h-3 w-3 ${checked ? "opacity-100" : "opacity-0"}`} strokeWidth={3} />
    )}
  </button>
);

const TanStackTable = ({
  data,
  columns,
  legacyColumns,
  sorting = [],
  onSortingChange,
  pageIndex = 0,
  pageSize = 15,
  pageCount = 1,
  onPageIndexChange = () => {},
  totalData = 0,
  itemType = "Items",
  noDataTitle,
  noDataMessage,
  onRowClick = () => {},
  loading = false,
  height,
  showPagination = true,
  enableColumnReorder = true,
  enableColumnPinning = true,
  enableColumnResizing = true,
  surfaceVariant = "legacy",
}: Props) => {
  const isLovableSurface = surfaceVariant === "lovable";
  const resolvedColumns = useMemo(
    () => columns || createLegacyTanStackColumns(legacyColumns || []),
    [columns, legacyColumns],
  );
  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>([]);
  const [columnPinning, setColumnPinning] = useState<ColumnPinningState>({ left: [], right: [] });
  const [draggingColumnId, setDraggingColumnId] = useState<string | null>(null);
  const [dropTargetColumnId, setDropTargetColumnId] = useState<string | null>(null);
  const [openHeaderMenuId, setOpenHeaderMenuId] = useState<string | null>(null);

  const baseLeafColumnIds = useMemo(
    () => resolvedColumns.map((column: any, index: number) => String(
      column?.id || column?.accessorKey || `column-${index}`,
    )),
    [resolvedColumns],
  );

  const table = useReactTable({
    data,
    columns: resolvedColumns,
    state: {
      sorting,
      pagination: {
        pageIndex,
        pageSize,
      },
      columnOrder,
      columnPinning,
    },
    manualSorting: true,
    manualPagination: true,
    pageCount,
    onSortingChange,
    onColumnOrderChange: setColumnOrder,
    onColumnPinningChange: setColumnPinning,
    enableColumnPinning,
    enableColumnResizing,
    columnResizeMode: "onChange",
    enableSortingRemoval: false,
    getCoreRowModel: getCoreRowModel(),
  });

  useEffect(() => {
    setColumnOrder((previous) => {
      const filtered = previous.filter((columnId) => baseLeafColumnIds.includes(columnId));
      const missing = baseLeafColumnIds.filter((columnId) => !filtered.includes(columnId));
      return [...filtered, ...missing];
    });
  }, [baseLeafColumnIds.join("|")]);

  useEffect(() => {
    if (!enableColumnPinning || !baseLeafColumnIds.length) return;
    setColumnPinning((previous) => {
      if (previous.left?.length) return previous;
      return {
        ...previous,
        left: [baseLeafColumnIds[0]],
      };
    });
  }, [enableColumnPinning, baseLeafColumnIds.join("|")]);

  const currentPage = pageIndex + 1;
  const safeTotalPages = Math.max(1, pageCount || 1);
  const rangeStart = totalData > 0 ? (pageIndex * pageSize) + 1 : 0;
  const rangeEnd = totalData > 0 ? Math.min(totalData, (pageIndex * pageSize) + data.length) : 0;
  const paginationTokens = useMemo(
    () => getPaginationTokens(currentPage, safeTotalPages),
    [currentPage, safeTotalPages],
  );

  const normalizedItemType = String(itemType || "Items");
  const displayItemType = normalizedItemType === "Items"
    ? "results"
    : normalizedItemType.toLowerCase();
  const countLabel = displayItemType === "accounts"
    ? `${displayItemType} with Profiles`
    : "results";
  const showLogoFooter = displayItemType === "accounts";
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);

  const moveColumn = useCallback((columnId: string, direction: "left" | "right") => {
    setColumnOrder((previous) => {
      const current = previous.length ? [...previous] : [...baseLeafColumnIds];
      const fromIndex = current.indexOf(columnId);
      if (fromIndex === -1) return current;
      const toIndex = direction === "left" ? fromIndex - 1 : fromIndex + 1;
      if (toIndex < 0 || toIndex >= current.length) return current;
      const [moved] = current.splice(fromIndex, 1);
      current.splice(toIndex, 0, moved);
      return current;
    });
  }, [baseLeafColumnIds]);

  const handleColumnDrop = useCallback((targetColumnId: string) => {
    if (!draggingColumnId || draggingColumnId === targetColumnId) return;
    setColumnOrder((previous) => {
      const current = previous.length ? [...previous] : [...baseLeafColumnIds];
      const fromIndex = current.indexOf(draggingColumnId);
      const toIndex = current.indexOf(targetColumnId);
      if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) return current;
      const [moved] = current.splice(fromIndex, 1);
      current.splice(toIndex, 0, moved);
      return current;
    });
    setDraggingColumnId(null);
    setDropTargetColumnId(null);
  }, [draggingColumnId, baseLeafColumnIds]);

  const getPinnedCellStyles = (column: Column<any, any>, isHeader = false) => {
    const pinState = column.getIsPinned();
    if (!pinState) return undefined;

    const shared: React.CSSProperties = {
      position: "sticky",
      zIndex: isHeader ? 25 : 15,
      background: "#fff",
    };

    if (pinState === "left") {
      return {
        ...shared,
        left: `${column.getStart("left")}px`,
        boxShadow: "1px 0 0 rgba(229,231,235,0.9)",
      };
    }

    return {
      ...shared,
      right: `${column.getAfter("right")}px`,
      boxShadow: "-1px 0 0 rgba(229,231,235,0.9)",
    };
  };

  const visibleRowIds = useMemo(
    () => data.map((row, index) => resolveRowSelectionId(row, index)),
    [data],
  );

  const selectedVisibleCount = useMemo(
    () => visibleRowIds.filter((id) => selectedRowIds.includes(id)).length,
    [visibleRowIds, selectedRowIds],
  );

  const allVisibleSelected = visibleRowIds.length > 0 && selectedVisibleCount === visibleRowIds.length;
  const someVisibleSelected = selectedVisibleCount > 0 && !allVisibleSelected;

  useEffect(() => {
    const handleDocumentClick = () => {
      setOpenHeaderMenuId(null);
    };

    document.addEventListener("click", handleDocumentClick);
    return () => document.removeEventListener("click", handleDocumentClick);
  }, []);

  useEffect(() => {
    setSelectedRowIds((prev) => prev.filter((id) => visibleRowIds.includes(id)));
  }, [visibleRowIds]);

  const toggleSelectRow = (rowId: string) => {
    setSelectedRowIds((prev) => (
      prev.includes(rowId)
        ? prev.filter((id) => id !== rowId)
        : [...prev, rowId]
    ));
  };

  const toggleSelectAllVisible = () => {
    if (allVisibleSelected) {
      setSelectedRowIds((prev) => prev.filter((id) => !visibleRowIds.includes(id)));
      return;
    }
    setSelectedRowIds((prev) => Array.from(new Set([...prev, ...visibleRowIds])));
  };

  const handleExportCsv = () => {
    if (!data.length || !selectedRowIds.length) return;
    const selectedSet = new Set(selectedRowIds);
    const exportRows = data.filter((row, index) => selectedSet.has(resolveRowSelectionId(row, index)));

    if (!exportRows.length) return;

    const exportColumns = table.getAllLeafColumns();
    const headerLine = exportColumns
      .map((column) => escapeCsvCell(getColumnHeaderLabel(column)))
      .join(",");

    const bodyLines = exportRows.map((row, rowIndex) => {
      const cells = exportColumns.map((column) => {
        const rawValue = getColumnRawValue(column, row, rowIndex);
        return escapeCsvCell(toExportValue(rawValue));
      });
      return cells.join(",");
    });

    const csvContent = [headerLine, ...bodyLines].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${displayItemType || "table"}-export.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const allRenderedRows = table.getRowModel().rows;

  return (
    <div className={cn(
      "relative rounded-lg overflow-hidden border",
      isLovableSurface ? "bg-card border-border" : "bg-white border-gray-200",
    )}
    >
      {loading && (
        <div className={cn(
          "absolute inset-0 z-20 flex items-center justify-center backdrop-blur-[1px]",
          isLovableSurface ? "bg-background/60" : "bg-white/65",
        )}
        >
          <span className={cn(
            "h-6 w-6 animate-spin rounded-full border-2 border-solid",
            isLovableSurface ? "border-primary/30 border-t-primary" : "border-purple-200 border-t-purple-600",
          )}
          />
        </div>
      )}

      <div className={cn(
        "px-5 py-3 border-b flex items-center justify-between gap-3",
        isLovableSurface ? "border-border bg-card" : "border-gray-200 bg-white",
      )}
      >
        <span className={cn("text-sm", isLovableSurface ? "text-muted-foreground" : "text-gray-500")}>
          <span className={cn("font-semibold", isLovableSurface ? "text-foreground" : "text-gray-900")}>{totalData}</span> {countLabel}
        </span>
        <div className="flex items-center gap-2">
          {selectedRowIds.length > 0 && (
            <button
              type="button"
              onClick={handleExportCsv}
              className={cn(
                "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium border transition-colors",
                isLovableSurface
                  ? "text-foreground border-border hover:bg-muted"
                  : "text-gray-700 border-gray-200 hover:bg-gray-50",
              )}
            >
              <Download className="h-3.5 w-3.5" />
              {`Export Selected (${selectedRowIds.length})`}
            </button>
          )}
        </div>
      </div>

      <div className="overflow-auto" style={height ? { maxHeight: height } : undefined}>
        <table className="min-w-full" style={{ width: table.getTotalSize() + 80 }}>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr
                key={headerGroup.id}
                className={cn(
                  "border-b",
                  isLovableSurface ? "border-border bg-card" : "border-gray-200 bg-white",
                )}
              >
                <th className="w-12 px-4 py-3 text-left">
                  <TableCheckbox
                    checked={allVisibleSelected}
                    indeterminate={someVisibleSelected}
                    onChange={toggleSelectAllVisible}
                    surfaceVariant={surfaceVariant}
                  />
                </th>
                {headerGroup.headers.map((header) => {
                  const canSort = header.column.getCanSort();
                  const isPinned = header.column.getIsPinned();
                  const canResize = header.column.getCanResize();
                  const pinnedStyles = getPinnedCellStyles(header.column, true);
                  return (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      onDragOver={(event) => {
                        if (!enableColumnReorder) return;
                        event.preventDefault();
                      }}
                      onDragEnter={(event) => {
                        if (!enableColumnReorder || !draggingColumnId) return;
                        event.preventDefault();
                        setDropTargetColumnId(header.column.id);
                      }}
                      onDrop={() => handleColumnDrop(header.column.id)}
                      style={{
                        width: header.getSize() || 100,
                        ...pinnedStyles,
                      }}
                      className={cn(
                        "group relative px-4 py-3 text-left text-sm font-medium whitespace-nowrap",
                        isLovableSurface ? "text-muted-foreground" : "text-gray-500",
                        draggingColumnId === header.column.id && "opacity-60",
                        dropTargetColumnId === header.column.id && (isLovableSurface ? "bg-muted" : "bg-gray-50"),
                      )}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <button
                          type="button"
                          className={cn(
                            "flex items-center gap-1.5 transition-colors",
                            canSort ? "cursor-pointer" : "cursor-default",
                            canSort && (isLovableSurface ? "hover:text-foreground" : "hover:text-gray-900"),
                          )}
                          onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                        >
                          <span className="whitespace-nowrap">
                            {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                          </span>
                          {canSort ? <ArrowUpDown className="h-2.5 w-2.5" strokeWidth={1.75} /> : null}
                        </button>

                        {(enableColumnReorder || enableColumnPinning) && (
                          <div className="relative flex items-center gap-1">
                            {enableColumnReorder && (
                              <button
                                type="button"
                                aria-label="Drag to reorder column"
                                title="Drag to reorder"
                                draggable
                                onDragStart={(event) => {
                                  event.stopPropagation();
                                  event.dataTransfer.setData("text/plain", header.column.id);
                                  event.dataTransfer.effectAllowed = "move";
                                  setDraggingColumnId(header.column.id);
                                }}
                                onDragEnd={(event) => {
                                  event.stopPropagation();
                                  setDraggingColumnId(null);
                                  setDropTargetColumnId(null);
                                }}
                                onClick={(event) => event.stopPropagation()}
                                className={cn(
                                  "h-6 w-6 rounded-md inline-flex items-center justify-center opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity cursor-grab active:cursor-grabbing",
                                  isLovableSurface
                                    ? "text-muted-foreground hover:text-foreground hover:bg-muted"
                                    : "text-gray-500 hover:text-gray-800 hover:bg-gray-100",
                                )}
                              >
                                <GripVertical className="h-3.5 w-3.5" />
                              </button>
                            )}
                            <button
                              type="button"
                              aria-haspopup="menu"
                              aria-expanded={openHeaderMenuId === header.column.id}
                              className={cn(
                                "h-6 w-6 rounded-md inline-flex items-center justify-center opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity",
                                isLovableSurface
                                  ? "text-muted-foreground hover:text-foreground hover:bg-muted"
                                  : "text-gray-500 hover:text-gray-800 hover:bg-gray-100",
                              )}
                              onClick={(event) => {
                                event.stopPropagation();
                                setOpenHeaderMenuId((current) => (current === header.column.id ? null : header.column.id));
                              }}
                            >
                              <MoreHorizontal className="h-3.5 w-3.5" />
                            </button>

                            {openHeaderMenuId === header.column.id && (
                              <div
                                role="menu"
                                tabIndex={-1}
                                className={cn(
                                  "absolute right-0 top-7 z-50 min-w-[150px] rounded-md border py-1 flex flex-col",
                                  isLovableSurface ? "border-border bg-popover shadow-md" : "border-gray-200 bg-white shadow-lg",
                                )}
                                onClick={(event) => event.stopPropagation()}
                              >
                                {enableColumnPinning && (
                                  <button
                                    type="button"
                                    role="menuitem"
                                    className={cn(
                                      "flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm transition-colors",
                                      isLovableSurface
                                        ? "text-foreground hover:bg-muted"
                                        : "text-gray-700 hover:bg-gray-50",
                                    )}
                                    onClick={() => {
                                      header.column.pin(isPinned ? false : "left");
                                      setOpenHeaderMenuId(null);
                                    }}
                                  >
                                    <Pin className="h-3.5 w-3.5" />
                                    {isPinned ? "Unpin Column" : "Pin Column"}
                                  </button>
                                )}

                                {enableColumnReorder && (
                                  <>
                                    <button
                                      type="button"
                                      role="menuitem"
                                      className={cn(
                                        "block w-full px-3 py-1.5 text-left text-sm transition-colors",
                                        isLovableSurface
                                          ? "text-foreground hover:bg-muted"
                                          : "text-gray-700 hover:bg-gray-50",
                                      )}
                                      onClick={() => {
                                        moveColumn(header.column.id, "left");
                                        setOpenHeaderMenuId(null);
                                      }}
                                    >
                                      Move Left
                                    </button>
                                    <button
                                      type="button"
                                      role="menuitem"
                                      className={cn(
                                        "block w-full px-3 py-1.5 text-left text-sm transition-colors",
                                        isLovableSurface
                                          ? "text-foreground hover:bg-muted"
                                          : "text-gray-700 hover:bg-gray-50",
                                      )}
                                      onClick={() => {
                                        moveColumn(header.column.id, "right");
                                        setOpenHeaderMenuId(null);
                                      }}
                                    >
                                      Move Right
                                    </button>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      {canResize && (
                        <div
                          role="presentation"
                          className={cn(
                            "absolute right-0 top-0 h-full w-2 cursor-col-resize touch-none select-none",
                            header.column.getIsResizing()
                              ? (isLovableSurface ? "bg-primary/30" : "bg-purple-300/70")
                              : (isLovableSurface ? "hover:bg-muted-foreground/20" : "hover:bg-gray-300/60"),
                          )}
                          onDoubleClick={() => header.column.resetSize()}
                          onMouseDown={header.getResizeHandler()}
                          onTouchStart={header.getResizeHandler()}
                        />
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody className={cn(
            "divide-y",
            isLovableSurface ? "divide-border bg-card" : "divide-gray-200 bg-white",
          )}
          >
            {allRenderedRows.length > 0 ? (
              <AnimatePresence initial={false}>
                {allRenderedRows.map((row) => {
                  const rowSelectionId = resolveRowSelectionId(row.original, row.index);
                  return (
                  <motion.tr
                    key={row.id}
                    layout
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -4 }}
                    transition={{
                      duration: 0.2,
                      ease: [0.22, 1, 0.36, 1],
                      delay: Math.min(row.index * 0.015, 0.12),
                    }}
                    onClick={() => onRowClick(row.original)}
                    className={cn(
                      "group cursor-pointer transition-colors duration-150",
                      isLovableSurface ? "hover:bg-muted" : "hover:bg-gray-50",
                    )}
                  >
                    <td className={cn(
                      "px-4 py-3 align-middle",
                      isLovableSurface ? "bg-card" : "bg-white",
                    )}
                    >
                      <div className="flex items-center gap-2">
                        <TableCheckbox
                          checked={selectedRowIds.includes(rowSelectionId)}
                          onChange={() => toggleSelectRow(rowSelectionId)}
                          surfaceVariant={surfaceVariant}
                        />
                      </div>
                    </td>
                    {row.getVisibleCells().map((cell) => {
                      const pinnedStyles = getPinnedCellStyles(cell.column, false);
                      return (
                      <td
                        key={cell.id}
                        className={cn(
                          "px-4 py-3 text-sm align-middle",
                          isLovableSurface ? "text-foreground bg-card" : "text-gray-900 bg-white",
                        )}
                        style={{
                          width: cell.column.getSize(),
                          ...pinnedStyles,
                        }}
                      >
                        <motion.div
                          layout
                          transition={{ duration: 0.2, ease: "easeOut" }}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </motion.div>
                      </td>
                      );
                    })}
                  </motion.tr>
                  );
                })}
              </AnimatePresence>
            ) : (
              <tr>
                <td colSpan={(table.getAllColumns().length || 1) + 1}>
                  <EmptyTablePlaceholder title={noDataTitle} message={noDataMessage} />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {showPagination && (
        <div className={cn(
          "px-5 py-3 border-t flex items-center justify-between",
          isLovableSurface ? "border-border bg-card" : "border-gray-200 bg-white",
        )}
        >
          <div className="flex items-center gap-2">
            <button
              type="button"
              className={cn(
                "h-7 w-7 rounded-full flex items-center justify-center transition-colors",
                currentPage === 1
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : (isLovableSurface ? "bg-primary text-primary-foreground hover:opacity-90" : "bg-purple-700 text-white hover:bg-purple-800"),
              )}
              onClick={() => currentPage > 1 && onPageIndexChange(currentPage - 2)}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className={cn("text-sm", isLovableSurface ? "text-muted-foreground" : "text-gray-500")}>
              <span className={cn("font-medium", isLovableSurface ? "text-foreground" : "text-gray-900")}>{rangeStart}-{rangeEnd}</span> of <span className={cn("font-medium", isLovableSurface ? "text-foreground" : "text-gray-900")}>{totalData}</span> {normalizedItemType}
            </span>
          </div>

          <div className="flex items-center gap-1">
            <button
              type="button"
              className={cn(
                "px-2.5 py-1.5 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 transition-colors",
                isLovableSurface ? "text-muted-foreground hover:text-foreground" : "text-gray-500 hover:text-gray-900",
              )}
              disabled={currentPage === 1}
              onClick={() => onPageIndexChange(Math.max(0, currentPage - 2))}
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </button>

            {(() => {
              let ellipsisCount = 0;
              return paginationTokens.map((token) => {
                if (token === "ellipsis") {
                  ellipsisCount += 1;
                  return (
                    <span className={cn("px-2", isLovableSurface ? "text-muted-foreground/70" : "text-gray-400")} key={`ellipsis-${ellipsisCount}`}>...</span>
                  );
                }
                return (
                  <button
                    key={`page-${token}`}
                    type="button"
                    className={cn(
                      "w-8 h-8 p-0 rounded-md text-sm transition-colors",
                      token === currentPage
                        ? (isLovableSurface ? "bg-primary text-primary-foreground" : "bg-purple-700 text-white")
                        : (isLovableSurface ? "text-muted-foreground hover:bg-muted" : "text-gray-600 hover:bg-gray-100"),
                    )}
                    onClick={() => onPageIndexChange(token - 1)}
                  >
                    {token}
                  </button>
                );
              });
            })()}

            <button
              type="button"
              className={cn(
                "px-2.5 py-1.5 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 transition-colors",
                isLovableSurface ? "text-foreground hover:text-foreground/80" : "text-gray-700 hover:text-gray-900",
              )}
              disabled={currentPage === safeTotalPages}
              onClick={() => onPageIndexChange(Math.min(safeTotalPages - 1, currentPage))}
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
      {showLogoFooter && (
        <div className={cn(
          "px-5 py-2 border-t",
          isLovableSurface ? "border-border bg-muted" : "border-gray-200 bg-gray-50",
        )}
        >
          <span className={cn("text-xs", isLovableSurface ? "text-muted-foreground" : "text-gray-500")}>
            Logos provided by <a href="https://logo.dev" className={cn(isLovableSurface ? "text-primary hover:text-primary/80" : "text-purple-700 hover:text-purple-900")}>Logo.dev</a>
          </span>
        </div>
      )}
    </div>
  );
};

export default TanStackTable;
